import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api';
import {
  buildNavigationConfig,
  buildNavigationConfigByCategories,
} from '@/lib/navigation/buildNavigationStructure';
import type { NavigationCategory, NavigationConfigItem } from '@/types/navigation';
import type { RoleNavigationPermission } from '@/types/organizations';

export type { NavigationCategory, NavigationConfigItem } from '@/types/navigation';

export interface ModuleAssignment {
  id: string;
  organization_id: string;
  module_path: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

const NAV_STALE_TIME = 5 * 60_000;

export const navigationConfigKeys = {
  all: ['navigation-config'] as const,
  categories: () => [...navigationConfigKeys.all, 'categories'] as const,
  permissions: () => [...navigationConfigKeys.all, 'permissions'] as const,
  moduleAssignments: (orgId?: string) =>
    [...navigationConfigKeys.all, 'module-assignments', orgId || 'all'] as const,
};

export const orgNavigationKeys = {
  all: ['org-navigation'] as const,
  byOrg: (orgId: string) => [...orgNavigationKeys.all, orgId] as const,
};

export const getOrgNavigation = (orgId: string) =>
  apiGet<{
    categories: NavigationCategory[];
    modules: NavigationConfigItem[];
    role_permissions: RoleNavigationPermission[];
  }>(`/organizations/${orgId}/navigation`);

export const getNavigationCategories = () =>
  apiGet<NavigationCategory[]>('/navigation/categories');

export const createNavigationCategory = (
  payload: Partial<NavigationCategory> & { name: string; slug?: string },
) => apiPost<NavigationCategory>('/navigation/categories', payload);

export const updateNavigationCategory = (id: string, payload: Partial<NavigationCategory>) =>
  apiPut<NavigationCategory>(`/navigation/categories/${id}`, payload);

export const deleteNavigationCategory = (id: string) =>
  apiDelete<{ deleted: boolean }>(`/navigation/categories/${id}`);

export const getNavigationPermissionsConfig = () =>
  apiGet<NavigationConfigItem[]>('/navigation/permissions-config');

export const createNavigationPermissionConfig = (payload: Partial<NavigationConfigItem>) =>
  apiPost<NavigationConfigItem>('/navigation/permissions-config', payload);

export const updateNavigationPermissionConfig = (
  id: string,
  payload: Partial<NavigationConfigItem>,
) => apiPut<NavigationConfigItem>(`/navigation/permissions-config/${id}`, payload);

export const deleteNavigationPermissionConfig = (id: string) =>
  apiDelete<{ deleted: boolean }>(`/navigation/permissions-config/${id}`);

export const getModuleAssignments = (orgId?: string) =>
  apiGet<ModuleAssignment[]>(
    orgId
      ? `/navigation/module-assignments?orgId=${encodeURIComponent(orgId)}`
      : '/navigation/module-assignments',
  );

export const createModuleAssignment = (payload: {
  organization_id: string;
  module_path: string;
  is_enabled: boolean;
}) => apiPost<ModuleAssignment>('/navigation/module-assignments', payload);

export const updateModuleAssignment = (
  id: string,
  payload: Partial<Pick<ModuleAssignment, 'organization_id' | 'module_path' | 'is_enabled'>>,
) => apiPut<ModuleAssignment>(`/navigation/module-assignments/${id}`, payload);

export const deleteModuleAssignment = (id: string) =>
  apiDelete<{ deleted: boolean }>(`/navigation/module-assignments/${id}`);

export const useNavigationCategoriesQuery = (enabled = true) =>
  useQuery({
    queryKey: navigationConfigKeys.categories(),
    queryFn: getNavigationCategories,
    enabled,
    staleTime: NAV_STALE_TIME,
  });

export const useNavigationPermissionsConfigQuery = (enabled = true) =>
  useQuery({
    queryKey: navigationConfigKeys.permissions(),
    queryFn: getNavigationPermissionsConfig,
    enabled,
    staleTime: NAV_STALE_TIME,
  });

export const useNavigationModuleAssignmentsQuery = (orgId?: string, enabled = true) =>
  useQuery({
    queryKey: navigationConfigKeys.moduleAssignments(orgId),
    queryFn: () => getModuleAssignments(orgId),
    enabled: enabled && Boolean(orgId),
    staleTime: NAV_STALE_TIME,
  });

export function useOrganizationNavigation(orgId?: string) {
  const { data: navData, isLoading: navLoading, error: navError } = useQuery({
    queryKey: orgNavigationKeys.byOrg(orgId ?? ''),
    queryFn: () => getOrgNavigation(orgId!),
    enabled: Boolean(orgId),
    staleTime: NAV_STALE_TIME,
  });

  const modules = navData?.modules ?? [];

  const navigationConfig = useMemo(
    () => buildNavigationConfig(modules),
    [modules],
  );

  const navigationConfigByCategories = useMemo(
    () => buildNavigationConfigByCategories(navData?.categories ?? [], modules),
    [navData?.categories, modules],
  );

  return {
    navigationConfig,
    navigationConfigByCategories,
    categories: navData?.categories ?? [],
    modules,
    rolePermissions: navData?.role_permissions ?? [],
    moduleAssignments: [],
    isLoading: navLoading,
    error: navError ?? null,
  };
}

export const useNavigationConfigMutations = () => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: navigationConfigKeys.all });
    void queryClient.invalidateQueries({ queryKey: orgNavigationKeys.all });
  };

  const createCategoryMutation = useMutation({
    mutationFn: createNavigationCategory,
    onSuccess: invalidate,
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<NavigationCategory> }) =>
      updateNavigationCategory(id, payload),
    onSuccess: invalidate,
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: deleteNavigationCategory,
    onSuccess: invalidate,
  });

  const createPermissionMutation = useMutation({
    mutationFn: createNavigationPermissionConfig,
    onSuccess: invalidate,
  });

  const updatePermissionMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<NavigationConfigItem> }) =>
      updateNavigationPermissionConfig(id, payload),
    onSuccess: invalidate,
  });

  const deletePermissionMutation = useMutation({
    mutationFn: deleteNavigationPermissionConfig,
    onSuccess: invalidate,
  });

  const createModuleAssignmentMutation = useMutation({
    mutationFn: createModuleAssignment,
    onSuccess: invalidate,
  });

  const updateModuleAssignmentMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<Pick<ModuleAssignment, 'organization_id' | 'module_path' | 'is_enabled'>>;
    }) => updateModuleAssignment(id, payload),
    onSuccess: invalidate,
  });

  const toggleOrganizationModuleMutation = useMutation({
    mutationFn: async ({
      organizationId,
      modulePath,
      isEnabled,
      allModules,
    }: {
      organizationId: string;
      modulePath: string;
      isEnabled: boolean;
      allModules: NavigationConfigItem[];
    }) => {
      const assignments = await getModuleAssignments(organizationId);
      const upsert = async (path: string, enabled: boolean) => {
        const existing = assignments.find((item) => item.module_path === path);
        if (existing) {
          await updateModuleAssignment(existing.id, { is_enabled: enabled });
          return;
        }
        await createModuleAssignment({
          organization_id: organizationId,
          module_path: path,
          is_enabled: enabled,
        });
      };

      await upsert(modulePath, isEnabled);

      const subItems = allModules.filter(
        (item) =>
          item.parent_path === modulePath &&
          item.organization_id === null &&
          item.is_active,
      );

      await Promise.all(subItems.map((subItem) => upsert(subItem.path, isEnabled)));
    },
    onSuccess: invalidate,
  });

  return {
    createCategoryMutation,
    updateCategoryMutation,
    deleteCategoryMutation,
    createPermissionMutation,
    updatePermissionMutation,
    deletePermissionMutation,
    createModuleAssignmentMutation,
    updateModuleAssignmentMutation,
    toggleOrganizationModuleMutation,
  };
};
